import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckIcon, XCircleIcon, ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, CalendarIcon } from 'lucide-react';
export type ComparisonType = 'none' | 'previous_period' | 'previous_week' | 'previous_month' | 'previous_year' | 'custom';
interface DateRange {
  start: Date;
  end: Date;
}
interface ComparisonSelectorProps {
  value: ComparisonType;
  onChange: (comparison: ComparisonType) => void;
  customRange?: DateRange | null;
  onCustomRangeChange?: (range: DateRange | null) => void;
}
const COMPARISON_OPTIONS: {
  value: ComparisonType;
  label: string;
}[] = [{
  value: 'previous_period',
  label: 'Previous period'
}, {
  value: 'previous_week',
  label: 'Previous week'
}, {
  value: 'previous_month',
  label: 'Previous month'
}, {
  value: 'previous_year',
  label: 'Previous year'
}, {
  value: 'custom',
  label: 'Custom'
}];
function getComparisonLabel(value: ComparisonType): string {
  const option = COMPARISON_OPTIONS.find(o => o.value === value);
  return option?.label || 'Compare';
}
export function ComparisonSelector({
  value,
  onChange,
  customRange,
  onCustomRangeChange
}: ComparisonSelectorProps) {
  const {
    t
  } = useTranslation('analytics');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const calendarRef = useRef<HTMLDivElement>(null);
  // Calendar state
  const today = new Date();
  const [leftMonth, setLeftMonth] = useState(new Date(today.getFullYear(), today.getMonth() - 1, 1));
  const [tempStart, setTempStart] = useState<Date | null>(customRange?.start || null);
  const [tempEnd, setTempEnd] = useState<Date | null>(customRange?.end || null);
  const [isSelectingEnd, setIsSelectingEnd] = useState(false);
  const [startInput, setStartInput] = useState('');
  const [endInput, setEndInput] = useState('');
  const isActive = value !== 'none';
  const isCustom = value === 'custom';
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
      if (calendarRef.current && !calendarRef.current.contains(event.target as Node)) {
        setIsCalendarOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  // Sync input fields with temp dates
  useEffect(() => {
    if (tempStart) {
      setStartInput(formatInputDate(tempStart));
    } else {
      setStartInput('');
    }
  }, [tempStart]);
  useEffect(() => {
    if (tempEnd) {
      setEndInput(formatInputDate(tempEnd));
    } else {
      setEndInput('');
    }
  }, [tempEnd]);
  const formatInputDate = (date: Date) => {
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const yyyy = date.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  };
  const handleDisable = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('none');
    onCustomRangeChange?.(null);
    setIsDropdownOpen(false);
  };
  const handleSelect = (type: ComparisonType) => {
    onChange(type);
    setIsDropdownOpen(false);
    if (type === 'custom') {
      // Reset temp dates when switching to custom
      setTempStart(customRange?.start || null);
      setTempEnd(customRange?.end || null);
    }
  };
  const handleToggleDropdown = () => {
    if (!isActive) {
      // If not active, just open dropdown to pick an option
      setIsDropdownOpen(true);
    } else {
      setIsDropdownOpen(!isDropdownOpen);
    }
  };
  // Calendar helpers
  const rightMonth = new Date(leftMonth.getFullYear(), leftMonth.getMonth() + 1, 1);
  const navigateMonth = (direction: 'prev' | 'next') => {
    setLeftMonth(new Date(leftMonth.getFullYear(), leftMonth.getMonth() + (direction === 'prev' ? -1 : 1), 1));
  };
  const getDaysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };
  const getFirstDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };
  const isSameDay = (a: Date | null, b: Date | null) => {
    if (!a || !b) return false;
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  };
  const isInRange = (date: Date) => {
    if (!tempStart || !tempEnd) return false;
    return date >= tempStart && date <= tempEnd;
  };
  const isToday = (date: Date) => {
    return isSameDay(date, today);
  };
  const isFutureDate = (date: Date) => {
    const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    return date > todayStart;
  };
  const handleDayClick = (date: Date) => {
    if (isFutureDate(date)) return;
    if (!isSelectingEnd || !tempStart) {
      setTempStart(date);
      setTempEnd(null);
      setIsSelectingEnd(true);
    } else {
      if (date < tempStart) {
        setTempStart(date);
        setTempEnd(tempStart);
      } else {
        setTempEnd(date);
      }
      setIsSelectingEnd(false);
    }
  };
  const handleApply = () => {
    if (tempStart && tempEnd) {
      onCustomRangeChange?.({
        start: tempStart,
        end: tempEnd
      });
      setIsCalendarOpen(false);
    }
  };
  const handleClear = () => {
    setTempStart(null);
    setTempEnd(null);
    setStartInput('');
    setEndInput('');
  };
  const formatMonthYear = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      month: 'long',
      year: 'numeric'
    });
  };
  const formatCustomRangeLabel = () => {
    if (customRange) {
      const fmt = (d: Date) => d.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
      return `${fmt(customRange.start)} - ${fmt(customRange.end)}`;
    }
    return 'Custom range';
  };
  const renderCalendarMonth = (monthDate: Date) => {
    const daysInMonth = getDaysInMonth(monthDate);
    const firstDay = getFirstDayOfMonth(monthDate);
    const days: (Date | null)[] = [];
    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(monthDate.getFullYear(), monthDate.getMonth(), i));
    }
    return <div className="w-[280px]" data-id="element-1595">
        <div className="text-center text-sm font-semibold text-gray-900 mb-3" data-id="element-1596">
          {formatMonthYear(monthDate)}
        </div>
        <div className="grid grid-cols-7 gap-0" data-id="element-1597">
          {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => <div key={day} className="text-center text-xs font-medium text-gray-500 py-2" data-id="element-1598">
              {day}
            </div>)}
          {days.map((date, idx) => {
          if (!date) {
            return <div key={`empty-${idx}`} className="h-9" data-id="element-1599" />;
          }
          const selected = isSameDay(date, tempStart) || isSameDay(date, tempEnd);
          const inRange = isInRange(date);
          const todayDate = isToday(date);
          const future = isFutureDate(date);
          return <button key={date.toISOString()} onClick={() => handleDayClick(date)} disabled={future} className={`h-9 w-full text-sm rounded-md transition-colors
                  ${future ? 'text-gray-300 cursor-not-allowed' : 'hover:bg-indigo-50 cursor-pointer'}
                  ${selected ? 'bg-indigo-600 text-white hover:bg-indigo-700' : ''}
                  ${inRange && !selected ? 'bg-indigo-50 text-indigo-700' : ''}
                  ${todayDate && !selected ? 'font-bold text-gray-900' : ''}
                  ${!selected && !inRange && !future ? 'text-gray-700' : ''}
                `} data-id="element-1600">
                {date.getDate()}
              </button>;
        })}
        </div>
      </div>;
  };
  return <div className="flex items-center gap-2" data-id="element-1601">
      {/* Main comparison button + dropdown */}
      <div className="relative" ref={dropdownRef} data-id="element-1602">
        <button onClick={handleToggleDropdown} className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md transition-colors ${isActive ? 'text-indigo-700 bg-indigo-50 border border-indigo-300 hover:bg-indigo-100' : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'}`} data-id="element-1603">
          {isActive && <XCircleIcon size={16} className="text-indigo-400 hover:text-indigo-600 cursor-pointer flex-shrink-0" onClick={handleDisable} data-id="element-1604" />}
          <span data-id="element-1605">{t('components.ComparisonSelector.compare')}</span>
          {isActive && <>
              <span className="text-indigo-300" data-id="element-1606">|</span>
              <span className="text-indigo-700 font-semibold" data-id="element-1607">
                {getComparisonLabel(value)}
              </span>
            </>}
          <ChevronDownIcon size={14} className={isActive ? 'text-indigo-500' : 'text-gray-400'} data-id="element-1608" />
        </button>

        {isDropdownOpen && <div className="absolute left-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50" data-id="element-1609">
            {COMPARISON_OPTIONS.map(option => <button key={option.value} onClick={() => handleSelect(option.value)} className={`w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors ${value === option.value ? 'text-indigo-600 font-medium' : 'text-gray-700'}`} data-id="element-1610">
                {option.label}
              </button>)}
          </div>}
      </div>

      {/* Custom range button (only visible when Custom is selected) */}
      {isCustom && <div className="relative" ref={calendarRef} data-id="element-1611">
          <button onClick={() => setIsCalendarOpen(!isCalendarOpen)} className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors" data-id="element-1612">
            <span data-id="element-1613">{formatCustomRangeLabel()}</span>
            <ChevronDownIcon size={14} className="text-gray-400" data-id="element-1614" />
          </button>

          {isCalendarOpen && <div className="absolute left-0 mt-2 bg-white rounded-xl shadow-xl border border-gray-200 p-6 z-50 w-auto" data-id="element-1615">
              {/* Start / End inputs */}
              <div className="flex items-center gap-4 mb-5" data-id="element-1616">
                <div className="flex items-center gap-2" data-id="element-1617">
                  <span className="text-sm text-gray-500 font-medium" data-id="element-1618">
                    {t('components.ComparisonSelector.start')}
                  </span>
                  <input type="text" aria-label={t('components.ComparisonSelector.start')} placeholder={t('components.ComparisonSelector.attrs.mm_dd_yyyy')} value={startInput} onChange={e => setStartInput(e.target.value)} className="px-3 py-1.5 text-sm border border-gray-300 rounded-md w-36 focus:ring-2 focus:ring-indigo-500 focus:border-transparent" data-id="element-1619" />
                </div>
                <div className="flex items-center gap-2" data-id="element-1620">
                  <span className="text-sm text-gray-500 font-medium" data-id="element-1621">
                    {t('components.ComparisonSelector.end')}
                  </span>
                  <input type="text" aria-label={t('components.ComparisonSelector.end')} placeholder={t('components.ComparisonSelector.attrs.mm_dd_yyyy')} value={endInput} onChange={e => setEndInput(e.target.value)} className="px-3 py-1.5 text-sm border border-gray-300 rounded-md w-36 focus:ring-2 focus:ring-indigo-500 focus:border-transparent" data-id="element-1622" />
                </div>
              </div>

              {/* Month navigation + calendars */}
              <div className="flex items-start gap-8" data-id="element-1623">
                <div data-id="element-1624">
                  <div className="flex items-center justify-between mb-2" data-id="element-1625">
                    <button onClick={() => navigateMonth('prev')} className="p-1 hover:bg-gray-100 rounded transition-colors" data-id="element-1626">
                      <ChevronLeftIcon size={18} className="text-gray-500" data-id="element-1627" />
                    </button>
                    <div className="flex-1" data-id="element-1628" />
                  </div>
                  {renderCalendarMonth(leftMonth)}
                </div>
                <div data-id="element-1629">
                  <div className="flex items-center justify-between mb-2" data-id="element-1630">
                    <div className="flex-1" data-id="element-1631" />
                    <button onClick={() => navigateMonth('next')} className="p-1 hover:bg-gray-100 rounded transition-colors" data-id="element-1632">
                      <ChevronRightIcon size={18} className="text-gray-500" data-id="element-1633" />
                    </button>
                  </div>
                  {renderCalendarMonth(rightMonth)}
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end gap-3 mt-5 pt-4 border-t border-gray-100" data-id="element-1634">
                <button onClick={handleClear} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors" data-id="element-1635">
                  {t('components.ComparisonSelector.clear')}
                </button>
                <button onClick={handleApply} disabled={!tempStart || !tempEnd} className={`px-4 py-2 text-sm font-medium text-white rounded-md transition-colors ${tempStart && tempEnd ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-indigo-300 cursor-not-allowed'}`} data-id="element-1636">
                  {t('components.content.apply')}
                </button>
              </div>
            </div>}
        </div>}
    </div>;
}